import { Button, Form } from "react-bootstrap";
import BinIcon from "../../../../components/icon/Icon";
import { LiteTradeSearchHeaderStyled, LiteTradeSearchStyled } from "./styled/LiteTradeSearchStyled";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLangContext } from "../../../../providers/LangProvider/LangProviderContext";
import classNames from "classnames";
import _ from "lodash";
import LiteTradeSearchResultList from "./LiteTradeSearchResultList";
import { useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { LOG_AF_EVENT_NAMES } from "../../../../app/appConstants";
import { getUserSelectedAccountType } from "../../../../app/appSelectors";
import { logAppsflyerAction } from "../../../../app/appActions";
import { ILiteTradeAssetSymbol } from "../../types/liteTradeReducer.t";

type ILiteTradeSearchProps = {
    children: JSX.Element | JSX.Element[],
    headerTitle?: string,
}

function LiteTradeSearch(props: ILiteTradeSearchProps) {
    const { children, headerTitle } = props;
    const { pathname } = useLocation();
    const { getLangPath } = useLangContext();
    const [searchValue, setSearchValue] = useState('');
    const [isSearchOpened, setIsSearchOpened] = useState(false);
    const isSearchOpenedRef = useRef(isSearchOpened);
    const [isSearchInited, setIsSearchInited] = useState(false);
    const selectedAccountType = useSelector(getUserSelectedAccountType);
    const inputRef = useRef(null);
    const dispatch = useDispatch();

    useEffect(() => {
        let timer: NodeJS.Timeout | undefined;
        if (isSearchOpened) {
            timer = setTimeout(() => {
                setIsSearchInited && setIsSearchInited(true);
            }, 300);
        }
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        }
    }, [isSearchOpened]);

    useEffect(() => {
        return () => {
            if (isSearchOpenedRef.current) {
                dispatch(logAppsflyerAction({
                    name: LOG_AF_EVENT_NAMES.SEARCH_CLOSE,
                    payload: {
                        account_type: selectedAccountType,
                        source: 'back',
                    }
                }));
            }
        }
    }, [dispatch, selectedAccountType]);

    const handleOpenSearch = useCallback(() => {
        if (!isSearchOpened) {
            isSearchOpenedRef.current = true;
            setIsSearchOpened(true);
        }
        dispatch(logAppsflyerAction({
            name: LOG_AF_EVENT_NAMES.SEARCH_OPEN,
            payload: {
                account_type: selectedAccountType,
                source: pathname,
            }
        }));
    }, [dispatch, isSearchOpened, pathname, selectedAccountType]);

    const handleClearSearchValue = useCallback(() => {
        setSearchValue('');
        inputRef.current.value = '';
    }, []);

    const handleCloseSearch = useCallback(() => {
        isSearchOpenedRef.current = false;
        setIsSearchOpened(false);
        setIsSearchInited(false);
        handleClearSearchValue();
        dispatch(logAppsflyerAction({
            name: LOG_AF_EVENT_NAMES.SEARCH_CLOSE,
            payload: {
                account_type: selectedAccountType,
                source: 'cancel',
            }
        }));
    }, [dispatch, handleClearSearchValue, selectedAccountType]);

    const handleAssetSelect = useCallback((symbol: ILiteTradeAssetSymbol) => {
        dispatch(logAppsflyerAction({
            name: LOG_AF_EVENT_NAMES.SEARCH_SELECT_ASSET,
            payload: {
                account_type: selectedAccountType,
                value: symbol,
            }
        }));
    }, [dispatch, selectedAccountType]);

    const handleDebouncedSearchChange = useMemo(() => _.debounce((e) => {
        setSearchValue(e.target.value);
    }, 500), []);

    return <>
        {
            !!headerTitle && <LiteTradeSearchHeaderStyled className={classNames({ hidden: isSearchOpened })}>
                {headerTitle}
            </LiteTradeSearchHeaderStyled>
        }
        <LiteTradeSearchStyled>
            <div className={'flex-1 position-relative'}>
                <BinIcon name="IcSearch" className={'search-icon'}/>
                <Form.Control
                        autoComplete={'off'}
                        size="sm"
                        defaultValue={searchValue}
                        type="text"
                        name="asset-search"
                        placeholder={getLangPath('lite_version.home.search')}
                        ref={inputRef}
                        onFocus={handleOpenSearch}
                        onChange={handleDebouncedSearchChange}
                />
                {isSearchOpened &&
                        <BinIcon className={'clear-icon'} name="IcXCircle" onClick={handleClearSearchValue}/>}
            </div>
            <Button variant={'outline'} onClick={handleCloseSearch}
                    className={classNames('cancel-button', { hidden: !isSearchOpened })}>
                {getLangPath('lite_version.portfolio.cancel')}
            </Button>
        </LiteTradeSearchStyled>
        {
            isSearchInited
                    ? <LiteTradeSearchResultList
                            onAssetSelect={handleAssetSelect}
                            searchValue={searchValue}
                    />
                    : children
        }
    </>
}

export default memo(LiteTradeSearch);
